/**
 * Werving → "Teams & teamleads" (alleen met recht Werving bewerken).
 *
 * Per team een naam en een of meer teamleads (uit Gebruikersbeheer). Teamleads beoordelen automatisch alle
 * vacatures van hun team; het MT beoordeelt altijd mee. Volgorde slepen, team toevoegen, archiveren.
 * Vangnetten: naam, teamleads, volgorde, toevoegen en archiveren → ongedaan maken (toast + Ctrl/Cmd+Z; de
 * sneltoets registreert de pagina Vacatures); verwijderen → bevestig (kan alleen zonder vacatures, anders
 * bieden we archiveren aan). "Werving-leesrecht geven" zet in Gebruikersbeheer alleen de override
 * Werving → bekijken voor die persoon (andere rechten blijven); dat bevestig je eerst.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle, FormControlLabel,
  IconButton, Menu, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { bevestig } from '../../utils/bevestig';
import { wervingApi } from './wervingApi';
import { ACCENT, INKT, KOLOM_BG, LIJN, STIL, nieuweTeamleads, volgordeNaSlepen, zonderLeesrecht } from './wervingLogica';

const UITLEG_LEESRECHT = 'Dit zet in Gebruikersbeheer een persoonlijke override "Werving – bekijken" voor deze persoon. '
  + 'Andere rechten (rol, overige overrides, bewerken) blijven zoals ze zijn. Intrekken kan in Gebruikersbeheer.';

function TeamRij({ team, mensen, naamVan, bezig, onLabel, onTeamleads, onLeesrecht, onArchiveer, onVerwijder }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: team.sleutel });
  const [label, setLabel] = useState(team.label);
  const [menu, setMenu] = useState(null);
  useEffect(() => { setLabel(team.label); }, [team.label]);
  const zonder = zonderLeesrecht(team);

  const bewaarLabel = () => {
    const nieuw = label.replace(/\s+/g, ' ').trim();
    if (nieuw === team.label) return;
    if (nieuw.length < 2) { setLabel(team.label); toast.error('Een teamnaam heeft minstens 2 tekens'); return; }
    onLabel(team, nieuw).catch(() => setLabel(team.label));
  };

  return (
    <Box ref={setNodeRef} sx={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1,
      display: 'flex', gap: 1.25, alignItems: 'flex-start', py: 1.5, borderTop: `1px solid ${LIJN}`, bgcolor: '#fff' }}>
      <IconButton size="small" aria-label={`${team.label} verslepen`} {...attributes} {...listeners}
        sx={{ mt: 0.5, cursor: 'grab', color: '#b6bcc4', '&:hover': { color: STIL } }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Box sx={{ flex: '0 1 220px', minWidth: 140 }}>
        <TextField size="small" fullWidth value={label} onChange={(e) => setLabel(e.target.value)} onBlur={bewaarLabel}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setLabel(team.label); }}
          inputProps={{ 'aria-label': 'Teamnaam', maxLength: 100 }}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: LIJN },
            '& input': { fontWeight: 700, color: INKT } }} />
        <Typography sx={{ fontSize: 12, color: STIL, pl: 1.75 }}>
          {team.vacatures ? `${team.vacatures} vacature${team.vacatures === 1 ? '' : 's'}` : 'nog geen vacatures'}
        </Typography>
      </Box>
      <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
        <Autocomplete multiple size="small" options={[...new Set([...mensen, ...team.teamleads])]} value={team.teamleads}
          getOptionLabel={naamVan}
          disabled={bezig} filterSelectedOptions onChange={(_, v) => onTeamleads(team, v)}
          renderInput={(p) => <TextField {...p} placeholder={team.teamleads.length ? '' : 'Teamlead kiezen'}
            inputProps={{ ...p.inputProps, 'aria-label': `Teamleads ${team.label}` }} />} />
        {zonder.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, color: STIL }}>
              {zonder.map((t) => t.naam).join(', ')} {zonder.length === 1 ? 'heeft' : 'hebben'} nog geen recht Werving (bekijken)
            </Typography>
            <Button size="small" onClick={() => onLeesrecht(team, zonder)} disabled={bezig}
              sx={{ textTransform: 'none', color: ACCENT, fontSize: 12.5, py: 0, minWidth: 0 }}>Geef werving-leesrecht</Button>
          </Box>
        )}
      </Box>
      <IconButton size="small" aria-label={`Meer voor ${team.label}`} onClick={(e) => setMenu(e.currentTarget)} sx={{ mt: 0.5, color: STIL }}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { setMenu(null); onArchiveer(team, true); }}>Archiveren</MenuItem>
        <MenuItem onClick={() => { setMenu(null); onVerwijder(team); }} sx={{ color: '#b91c1c' }}>Verwijderen…</MenuItem>
      </Menu>
    </Box>
  );
}

export default function TeamsBeheer({ open, onClose, onGewijzigd }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mensenLijst, setMensenLijst] = useState([]);
  const [nieuw, setNieuw] = useState('');
  const [bezig, setBezig] = useState(false);
  const [autoRecht, setAutoRecht] = useState(false);
  const [archiefOpen, setArchiefOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const laad = useCallback(() => wervingApi.teams().then(setData).catch((e) => toast.error(e.message)), []);
  useEffect(() => {
    if (!open) return;
    setData(null);
    laad();
    wervingApi.mensen().then((d) => setMensenLijst(d.mensen || [])).catch(() => setMensenLijst([]));
  }, [open, laad]);

  const naamVan = (e) => mensenLijst.find((m) => m.email === e)?.naam
    || (data?.teams || []).flatMap((t) => t.teamleads_info || []).find((t) => t.email === e)?.naam || e;
  const klaar = async () => { await laad(); onGewijzigd?.(); };
  const teams = data?.teams || [];
  const actief = teams.filter((t) => !t.gearchiveerd);
  const archief = teams.filter((t) => t.gearchiveerd);

  // Elke wijziging: server eerst, dan herladen; undo zet de vorige waarde terug (geen toggle).
  const wijzig = async (team, body, tekst, terug) => {
    setBezig(true);
    try {
      await wervingApi.teamWijzig(team.sleutel, body);
      await klaar();
      biedOngedaanAan(tekst, async () => { await wervingApi.teamWijzig(team.sleutel, terug); await klaar(); });
    } catch (e) { toast.error(e.message); throw e; } finally { setBezig(false); }
  };

  const geefLeesrecht = async (team, wie, { vraag = true } = {}) => {
    if (!wie.length) return;
    if (vraag) {
      const ok = await bevestig({
        titel: 'Werving-leesrecht geven',
        tekst: `${wie.map((t) => t.naam || t.email).join(', ')} ${wie.length === 1 ? 'krijgt' : 'krijgen'} het recht Werving (bekijken): `
          + `${wie.length === 1 ? 'hij of zij ziet' : 'ze zien'} dan de vacatures van ${team.label} en kan beoordelen. ${UITLEG_LEESRECHT}`,
        knop: 'Leesrecht geven',
      });
      if (!ok) return;
    }
    setBezig(true);
    try {
      const uit = await Promise.all(wie.map((t) => wervingApi.leesrecht(team.sleutel, t.email || t)));
      const n = uit.filter((x) => x.gewijzigd).length;
      toast.success(n ? `Leesrecht gegeven aan ${n} ${n === 1 ? 'persoon' : 'personen'} (intrekken: Gebruikersbeheer)` : 'Had al leesrecht');
      await laad();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const zetTeamleads = async (team, lijst) => {
    const oud = team.teamleads;
    try {
      await wijzig(team, { teamleads: lijst }, `Teamleads ${team.label} bijgewerkt`, { teamleads: oud });
    } catch { return; }
    const erbij = nieuweTeamleads(oud, lijst);
    if (autoRecht && erbij.length) {
      // Vinkje staat aan: de uitleg staat bij het vinkje, dus hier geen tweede vraag.
      const t = (await wervingApi.teams().catch(() => null))?.teams?.find((x) => x.sleutel === team.sleutel);
      const zonder = zonderLeesrecht(t).filter((x) => erbij.includes(x.email));
      await geefLeesrecht(team, zonder, { vraag: false });
    }
  };

  const archiveer = (team, aan) => wijzig(team, { gearchiveerd: aan },
    aan ? `${team.label} gearchiveerd` : `${team.label} teruggezet`, { gearchiveerd: !aan }).catch(() => {});

  const verwijder = async (team) => {
    if (team.vacatures > 0) {
      const ok = await bevestig({
        titel: `${team.label} archiveren?`,
        tekst: `Er ${team.vacatures === 1 ? 'hangt 1 vacature' : `hangen ${team.vacatures} vacatures`} aan ${team.label}, dus verwijderen kan niet. `
          + 'Archiveren haalt het team uit de keuzelijst voor nieuwe vacatures; bestaande vacatures houden het team en de teamleads blijven beoordelaar.',
        knop: 'Archiveren',
      });
      if (ok) await archiveer(team, true);
      return;
    }
    const ok = await bevestig({
      titel: `${team.label} verwijderen?`, gevaarlijk: true, knop: 'Verwijderen',
      tekst: `Het team ${team.label} en de teamleads ervan worden definitief verwijderd. Er hangen geen vacatures aan.`,
    });
    if (!ok) return;
    setBezig(true);
    try { await wervingApi.teamVerwijder(team.sleutel); toast.success(`${team.label} verwijderd`); await klaar(); }
    catch (e) {
      toast.error(e.message);
      await laad();
    } finally { setBezig(false); }
  };

  const voegToe = async () => {
    const label = nieuw.replace(/\s+/g, ' ').trim();
    if (label.length < 2) { toast.error('Geef het team een naam'); return; }
    setBezig(true);
    try {
      const t = await wervingApi.teamMaak({ label });
      setNieuw('');
      await klaar();
      biedOngedaanAan(`${t.label} toegevoegd`, async () => { await wervingApi.teamVerwijder(t.sleutel); await klaar(); });
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const naSlepen = async ({ active, over }) => {
    if (!over) return;
    const volgorde = volgordeNaSlepen(teams, active.id, over.id);
    if (!volgorde) return;
    const oud = teams.map((t) => t.sleutel);
    setData((d) => ({ ...d, teams: volgorde.map((s) => d.teams.find((t) => t.sleutel === s)) }));
    try {
      await wervingApi.teamsVolgorde(volgorde);
      onGewijzigd?.();
      biedOngedaanAan('Volgorde aangepast', async () => { await wervingApi.teamsVolgorde(oud); await klaar(); });
    } catch (e) { toast.error(e.message); await laad(); }
  };

  const meldingGezien = async () => {
    try { await wervingApi.teamsMeldingGezien(); await laad(); } catch (e) { toast.error(e.message); }
  };

  const mensen = mensenLijst.map((m) => m.email);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>Teams & teamleads</Box>
        <IconButton aria-label="Sluiten" onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13.5, color: STIL, mb: 2 }}>
          Teamleads beoordelen automatisch elke vacature van hun team; het MT beoordeelt altijd mee. Sleep om de volgorde
          in de keuzelijst te veranderen.
        </Typography>
        {Array.isArray(data?.mt) && (
          <Typography sx={{ fontSize: 12.5, color: STIL, mt: -1, mb: 2 }}>
            MT: {data.mt.length ? data.mt.map((m) => m.naam).join(', ') : 'nog niemand'} — de MT-groep wordt beheerd in
            Admin → Gebruikersbeheer → Groepen.
          </Typography>
        )}

        {data?.melding && (
          <Box sx={{ mb: 2, px: 2, py: 1.5, border: `1px solid ${LIJN}`, borderRadius: 2, bgcolor: KOLOM_BG }}>
            <Typography sx={{ fontSize: 13.5, color: INKT }}>
              Bij de nieuwe indeling stonden deze vacatures op Camerateam. Gaat er een over regie? Zet hem dan op Regisseursteam.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 0.75 }}>
              {data.melding.camera_vacatures.map((v) => (
                <Typography key={v.id} role="button" tabIndex={0} onClick={() => navigate(`/werving/vacature/${v.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/werving/vacature/${v.id}`); }}
                  sx={{ fontSize: 13.5, fontWeight: 600, color: ACCENT, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                  {v.titel}
                </Typography>
              ))}
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={meldingGezien} sx={{ textTransform: 'none', color: STIL }}>Gezien</Button>
            </Box>
          </Box>
        )}

        <FormControlLabel sx={{ alignItems: 'flex-start', mb: 1.5, ml: 0 }}
          control={<Checkbox size="small" checked={autoRecht} onChange={(e) => setAutoRecht(e.target.checked)} sx={{ pt: 0.25 }} />}
          label={(
            <Box>
              <Typography sx={{ fontSize: 13.5, color: INKT }}>Geef nieuwe teamleads meteen werving-leesrecht</Typography>
              <Typography sx={{ fontSize: 12.5, color: STIL }}>{UITLEG_LEESRECHT}</Typography>
            </Box>
          )} />

        {!data ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box> : (
          <Box>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={naSlepen}>
              <SortableContext items={actief.map((t) => t.sleutel)} strategy={verticalListSortingStrategy}>
                {actief.map((t) => (
                  <TeamRij key={t.sleutel} team={t} mensen={mensen} naamVan={naamVan} bezig={bezig}
                    onLabel={(team, label) => wijzig(team, { label }, `Naam gewijzigd in ${label}`, { label: team.label })}
                    onTeamleads={zetTeamleads} onLeesrecht={(team, wie) => geefLeesrecht(team, wie)}
                    onArchiveer={archiveer} onVerwijder={verwijder} />
                ))}
              </SortableContext>
            </DndContext>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pt: 1.5, borderTop: `1px solid ${LIJN}` }}>
              <TextField size="small" placeholder="Nieuw team, bijv. Soundteam" value={nieuw} onChange={(e) => setNieuw(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }} sx={{ flex: '0 1 320px' }} inputProps={{ maxLength: 100 }} />
              <Button size="small" variant="outlined" onClick={voegToe} disabled={bezig || nieuw.trim().length < 2}
                sx={{ textTransform: 'none', fontWeight: 600, color: ACCENT, borderColor: '#cfd6e0' }}>Toevoegen</Button>
            </Box>

            {archief.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Button size="small" onClick={() => setArchiefOpen((x) => !x)} sx={{ textTransform: 'none', color: STIL, px: 0 }}>
                  {archiefOpen ? 'Gearchiveerd verbergen' : `Gearchiveerd (${archief.length})`}
                </Button>
                {archiefOpen && archief.map((t) => (
                  <Box key={t.sleutel} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: `1px solid ${LIJN}` }}>
                    <Typography sx={{ flex: 1, color: STIL }}>
                      {t.label}
                      <Box component="span" sx={{ fontSize: 12.5 }}>
                        {t.vacatures ? ` · ${t.vacatures} vacature${t.vacatures === 1 ? '' : 's'}` : ''}
                        {t.teamleads.length ? ` · ${t.teamleads.map(naamVan).join(', ')}` : ''}
                      </Box>
                    </Typography>
                    <Button size="small" onClick={() => archiveer(t, false)} disabled={bezig} sx={{ textTransform: 'none', color: ACCENT }}>Terugzetten</Button>
                    <Tooltip title={t.vacatures ? 'Er hangen vacatures aan dit team' : ''}>
                      <span>
                        <Button size="small" onClick={() => verwijder(t)} disabled={bezig || t.vacatures > 0}
                          sx={{ textTransform: 'none', color: '#b91c1c' }}>Verwijderen…</Button>
                      </span>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
